import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PersonalizedRecommendationsCard = ({ recommendations, onViewAll, onSaveRecommendation }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success-600 bg-success-50';
    if (confidence >= 70) return 'text-warning-600 bg-warning-50';
    return 'text-primary-600 bg-primary-50';
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft border border-accent-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-accent-800">AI Recommendations</h3>
            <p className="text-sm text-accent-600">Personalized for you</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={onViewAll}
          className="text-secondary hover:text-secondary-700"
        >
          View All
        </Button>
      </div>

      <div className="space-y-4">
        {recommendations.slice(0, 3).map((rec) => (
          <div key={rec.id} className="flex gap-4 p-4 bg-surface rounded-xl hover:bg-accent-50 transition-colors duration-200">
            <Image
              src={rec.image}
              alt={rec.name}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-accent-800 truncate pr-2">{rec.name}</h4>
                <div className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getConfidenceColor(rec.confidence)}`}>
                  {rec.confidence}% match
                </div>
              </div>
              <p className="text-sm text-accent-600 mb-2">Perfect for {rec.recipient}</p>
              <p className="text-xs text-accent-500 mb-3 line-clamp-2">{rec.reason}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-accent-800">{formatPrice(rec.price)}</span>
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={12} className="text-warning-400 fill-current" />
                    <span className="text-xs text-accent-600">{rec.rating}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Heart"
                    onClick={() => onSaveRecommendation(rec)}
                    className="text-accent-500 hover:text-primary"
                  />
                  <Button
                    variant="primary"
                    size="xs"
                    iconName="ShoppingCart"
                    className="shadow-soft"
                  >
                    Add to Cart
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {recommendations.length === 0 && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Sparkles" size={24} className="text-accent-400" />
          </div>
          <p className="text-accent-600 mb-2">No recommendations yet</p>
          <p className="text-sm text-accent-500">Complete your profile to get personalized suggestions</p>
        </div>
      )}
    </div>
  );
};

export default PersonalizedRecommendationsCard;